import { useState } from 'react'
import { useEditor } from '../store/useEditor'
import type { StrandMetrics } from '../render/scene'
import Approval from './Approval'
import Materials from './Materials'
import Packages from './Packages'
import Sections from './Sections'
import { approvalBlockers, currentApproval, driftFrom } from '../lib/approval'
import { gateWording, sectionTotals, sectionsOf } from '../lib/sections'
import { Sheet } from './bits'

type Tab = 'places' | 'takes' | 'scope'

/**
 * WHAT ANYBODY ACTUALLY KNOWS ABOUT THIS HOUSE.
 *
 * A sheet rather than a panel, for two reasons that come from the same place.
 * The bar along the bottom is three buttons on purpose and the argument for
 * keeping it three is written in Dock.tsx; measuring is not a drawing tool and
 * does not belong on it. And in the field this is opened standing in a
 * driveway on a phone, where a side panel is a strip and a sheet is the screen.
 *
 * THREE TABS, IN THE ORDER THE WORK HAPPENS. The places, what they take, and
 * the yes. Stacked in one column they were a scroll long enough that the count
 * and the scope were below the fold on every phone, which is the same as not
 * shipping them. They are three screens in the specification for the same
 * reason.
 *
 * Each tab carries a mark when it needs attention, so nobody has to open all
 * three to find out whether anything is wrong.
 */
export default function MeasureSheet({
  stats,
  onClose,
}: {
  stats: StrandMetrics[]
  onClose: () => void
}) {
  const doc = useEditor((s) => s.doc)
  const [tab, setTab] = useState<Tab>('places')

  const sections = sectionsOf(doc)
  const totals = sectionTotals(sections)
  const gate = gateWording(totals)
  const approved = currentApproval(doc)
  const drifted = approved ? driftFrom(approved, sections).length > 0 : false
  const blocked = sections.length > 0 && approvalBlockers(sections).length > 0

  const tabs: { id: Tab; label: string; flag: boolean }[] = [
    { id: 'places', label: 'Places', flag: !!gate },
    { id: 'takes', label: 'What it takes', flag: false },
    { id: 'scope', label: 'The scope', flag: drifted || (blocked && !!approved) },
  ]

  return (
    <Sheet
      title="Measurements"
      variant="measure-sheet"
      onClose={onClose}
    >
      {sections.length === 0 ? (
        /*
          A job with nothing on it does not need three tabs to say so. There
          is one thing to do and it is on the only tab that has anything.
        */
        <>
          {/*
            AN EMPTY JOB IS NOT AN ERROR. This was the same alarm red the
            product uses for a scope that has drifted, so the screen that
            teaches you what a section is opened by looking broken. Nothing
            is wrong here; the job is new.
          */}
          <p className="sec-gate is-empty">{gate}</p>
          <Sections stats={stats} />
        </>
      ) : (
        <>
          <div className="ms-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                className={tab === t.id ? 'ms-tab on' : 'ms-tab'}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {/* A dot, not a count. The number is on the tab itself. */}
                {t.flag && <i className="ms-flag" aria-label="needs attention" />}
              </button>
            ))}
          </div>

          {tab === 'places' && <Sections stats={stats} />}
          {tab === 'takes' && (
            <>
              <Materials />
              <Packages />
            </>
          )}
          {tab === 'scope' && <Approval />}
        </>
      )}
    </Sheet>
  )
}
